import { useNavigate, useParams } from 'react-router-dom';
import { Box, Heading, useToast, Center, Spinner, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import TriviaQuestionForm from './Form';
import useTrivia from './hooks/useTrivia';
import { ROUTES } from '../../consts/routes';
import { TriviaQuestionTemplate } from '@olagg/db-types';

const EditTriviaQuestion = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const params = useParams();
  const { getTriviaTemplate, updateTriviaTemplate, loading } = useTrivia();

  const [template, setTemplate] = useState<TriviaQuestionTemplate | null>(null);
  const [loadingTemplate, setLoadingTemplate] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTemplate = async () => {
      if (!params.id) {
        setError('ID de template no encontrado');
        setLoadingTemplate(false);
        return;
      }

      try {
        const data = await getTriviaTemplate(params.id);
        setTemplate(data);
      } catch (err: any) {
        setError(err?.response?.data?.error || 'Template no encontrado');
      } finally {
        setLoadingTemplate(false);
      }
    };

    fetchTemplate();
  }, [params.id]);

  const handleSubmit = async (data: any) => {
    if (!params.id) return;

    try {
      const templateData = {
        template: {
          title: data.title,
          text: data.text || '',
          explanation: data.explanation || '',
          points: data.points,
          time_limit_seconds: data.time_limit_seconds || null,
          speed_bonus_enabled: data.speed_bonus_enabled,
          speed_bonus_multiplier: data.speed_bonus_enabled ? data.speed_bonus_multiplier : null,
          extra_data: data.extra_data,
          trivia_template_answers_attributes: data.trivia_answers_attributes.map((answer: any, index: number) => ({
            ...(answer.id ? { id: answer.id } : {}),
            text: answer.text,
            is_correct: answer.is_correct,
            position: index + 1,
            extra_data: answer.extra_data
          }))
        }
      };

      await updateTriviaTemplate(params.id, templateData);
      toast({
        title: 'Template actualizado',
        description: 'El template de trivia se ha actualizado correctamente',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
      navigate(ROUTES.TRIVIA.all);
    } catch (error: any) {
      toast({
        title: 'Error al actualizar',
        description: error?.response?.data?.error || error?.message || 'No se pudo actualizar el template',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  const handleCancel = () => {
    navigate(ROUTES.TRIVIA.all);
  };

  if (loadingTemplate) {
    return (
      <Center h="80vh" color="white">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (error || !template) {
    return (
      <Center h="80vh" color="white">
        <Text>{error || 'Template no encontrado'}</Text>
      </Center>
    );
  }

  // Convertir template a formato esperado por el formulario
  const formData = {
    id: template.id,
    uid: template.uid,
    title: template.title,
    text: template.text,
    explanation: template.explanation || '',
    points: template.points,
    date: template.assignedDates?.[0] || new Date().toISOString().split('T')[0],
    position: 1,
    timeLimitSeconds: template.timeLimitSeconds,
    active: true,
    speedBonusEnabled: template.speedBonusEnabled,
    speedBonusMultiplier: String(template.speedBonusMultiplier),
    extraData: template.extraData || {},
    answersCount: template.triviaTemplateAnswers?.length || 0,
    responsesCount: template.usageCount || 0,
    createdAt: template.createdAt,
    updatedAt: template.updatedAt,
    triviaAnswers: template.triviaTemplateAnswers?.map(answer => ({
      id: answer.id,
      uid: answer.uid,
      text: answer.text,
      isCorrect: answer.isCorrect,
      position: answer.position,
      extraData: answer.extraData
    })) || []
  };

  return (
    <Center w="full" pb={4}>
      <Box color="white" w="full" px={4}>
        <Heading textAlign="center" mb={6} as="h1">
          Editar Template de Trivia
        </Heading>

        <TriviaQuestionForm
          initialData={formData as any}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={loading}
          isEdit={true}
        />
      </Box>
    </Center>
  );
};

export default EditTriviaQuestion;
